import { useState, useEffect, useCallback, useMemo } from 'react'
import { fetchDepartments, type Department } from '../api/catalog'
import {
  fetchCommissionRoles,
  fetchCommissionAssignments,
  fetchCommissionChain,
  createCommissionAssignment,
  closeCommissionAssignment,
  overrideCommissionAssignment,
  type CommissionRole,
  type UserCommissionAssignment,
  type CommissionChainResult,
  type CommissionChainTruncationReason,
  type CommissionChainNode,
} from '../api/commissions'
import { userDirectoryApi, type DirectoryUser } from '../api/userDirectory'
import { fetchMe, type LoginResponse } from '../api/auth'
import {
  FormGrid, Page, PageHeader, Callout, Card, CardHeader, CardContent, DataTable, LoadingState, EmptyState, StatusPill,
  Dialog, Button, TextField, Select, Icon, FilterBar,
} from '../components/ui'

/**
 * TASK-0262 (F-008). Same page-local, duplicated-on-purpose UI affordance as
 * `CommissionPlanRatesPage.tsx`'s own `isAdmin` — mirrors `PolicyNames.Admin`'s `RequireRole(...)`
 * set exactly. The server (`CommissionAssignmentsController`'s three `[Authorize(PolicyNames.Admin)]`
 * actions) is the actual boundary (criterion 6 — "il server resta l'unica difesa").
 */
function isAdmin(auth: LoginResponse | null): boolean {
  return auth !== null && ['Administrator', 'EqualAdmin'].includes(auth.role)
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function formatPercentage(value: number): string {
  return `${value.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
}

function formatDateTime(value: string): string {
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString('it-IT')
}

/** Same redacted-value affordance as `CommissionPlanRatesPage.tsx`'s own `Locked`. */
function Locked({ title }: { title: string }) {
  return (
    <span title={title}>
      <Icon name="lock" size={18} />
    </span>
  )
}

/**
 * D-01: renders the row's resolved percentage exactly as the card's own "prima → dopo" describes it
 * — "override 45% approvato da … il …" oppure "da listino: 40%" — with the two remaining
 * `effectiveSource` values (no active listino row / an internally inconsistent listino) surfaced as
 * their own explicit, non-alarming states rather than a blank cell.
 */
function EffectivePercentageCell({ row }: { row: UserCommissionAssignment }) {
  if (row.effectivePercentage === undefined) {
    return <Locked title="Percentuale non visibile" />
  }
  switch (row.effectiveSource) {
    case 'override':
      return (
        <span>
          <StatusPill tone="warning">override</StatusPill>{' '}
          {formatPercentage(row.effectivePercentage!)}
          <div className="text-muted md-typescale-body-small">
            approvato da #{row.overrideApprovedByUserId}
            {row.overrideApprovedAt ? ` il ${formatDateTime(row.overrideApprovedAt)}` : ''}
            {row.overrideReason ? ` — ${row.overrideReason}` : ''}
          </div>
        </span>
      )
    case 'listino':
      return (
        <span>
          <span className="text-muted">da listino:</span> {formatPercentage(row.effectivePercentage!)}
          {row.effectiveAsOf && <div className="text-muted md-typescale-body-small">al {row.effectiveAsOf}</div>}
        </span>
      )
    case 'nessuna_versione_attiva':
      return (
        <span className="text-muted fst-italic">
          nessuna versione di listino attiva{row.effectiveAsOf ? ` al ${row.effectiveAsOf}` : ''}
        </span>
      )
    case 'listino_incoerente':
      return (
        <span className="text-danger fst-italic" title="Più versioni attive del listino si sovrappongono per questa cella">
          listino incoerente{row.effectiveAsOf ? ` al ${row.effectiveAsOf}` : ''}
        </span>
      )
    default:
      return <span className="text-muted">—</span>
  }
}

/**
 * TASK-0263 (F-009/P-18, REQ-018-004, REQ-058-012). Human-readable copy for each
 * `CommissionChainTruncationReason` the backend can report — R-05: a walk that stopped early means
 * somebody who should have been considered was not, so it must be visible, never a silently short
 * list. `max_depth` names the real cap (`CommissionChainSplitter.MaxDepth`/`CommissionChainResolver.MaxDepth`
 * on the backend, currently 10) rather than leaving it unexplained.
 */
const CHAIN_TRUNCATION_MESSAGES: Record<Exclude<CommissionChainTruncationReason, 'none'>, string> = {
  no_assignment_for_seller:
    'Nessuna assegnazione trovata per questo utente su questo settore, a questa data: non c’è una catena da mostrare.',
  manager_without_assignment_in_department:
    'La catena si interrompe: un manager più in alto non ha un’assegnazione su questo settore, a questa data.',
  max_depth: 'La catena supera la profondità massima (10 livelli) ed è stata troncata a questo punto.',
  cycle: 'La catena contiene un ciclo (una persona compare due volte al suo interno) ed è stata troncata.',
  missing_signed_at: 'Data mancante: impossibile risolvere la catena senza una data.',
  assegnazioni_incoerenti:
    'Esistono due o più assegnazioni sovrapposte per la stessa persona sullo stesso settore, a questa ' +
    'data: i dati vanno corretti prima di poter calcolare la catena.',
}

/**
 * TASK-0263 correction (independent reviewer, note 2). Renders one resolved node's percentage cell,
 * distinguishing the THREE distinct reasons a node can carry no percentage at all — `'nessuna'` (no
 * active listino version for this cell) and `'listino_incoerente'` (two overlapping ACTIVE versions,
 * a data fault) are DELIBERATELY different sources, mirroring the same distinction
 * `EffectivePercentageCell` already draws for `'nessuna_versione_attiva'` vs. `'listino_incoerente'`
 * on the table above: collapsing them into one generic "nodo di passaggio" message would hide from
 * the operator exactly the fault that note 2 asked to surface.
 */
function ChainNodePercentage({ node }: { node: CommissionChainNode }) {
  if (node.percentage === undefined) {
    return <Locked title="Percentuale non visibile" />
  }
  if (node.percentage !== null) {
    return (
      <span>
        <StatusPill tone="neutral">{node.source}</StatusPill>{' '}
        {formatPercentage(node.percentage)}
      </span>
    )
  }
  if (node.source === 'listino_incoerente') {
    return (
      <span className="text-danger fst-italic" title="Più versioni attive del listino si sovrappongono per questa cella">
        nodo di passaggio — listino incoerente
      </span>
    )
  }
  return <span className="text-muted fst-italic">nodo di passaggio (nessuna versione di listino attiva)</span>
}

/**
 * TASK-0263 (F-009/P-18). "Anteprima catena": per un settore e una data scelti, mostra la catena di
 * riparto risalendo `ManagerUserId` dal venditore selezionato in alto (`CommissionChainResolver`,
 * `GET /api/commission-chain`) — la stessa gerarchia "che paga" (D-05) che `CommissionChainSplitter`
 * userà per dividere una provvigione reale.
 */
function ChainPreviewPanel({
  userId,
  departments,
  roleName,
  userName,
}: {
  userId: number
  departments: Department[]
  roleName: (id: number) => string
  userName: (id: number) => string
}) {
  const activeDepartments = useMemo(
    () => [...departments].filter(d => d.isActive).sort((a, b) => a.name.localeCompare(b.name)),
    [departments],
  )

  const [departmentId, setDepartmentId] = useState<number | ''>('')
  const [date, setDate] = useState(todayIso())
  const [result, setResult] = useState<CommissionChainResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (departmentId === '' || !date) {
      setResult(null)
      setError(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    fetchCommissionChain({ userId, departmentId, date })
      .then(r => {
        if (!cancelled) setResult(r)
      })
      .catch(e => {
        if (!cancelled) {
          setResult(null)
          setError(e instanceof Error ? e.message : 'Errore durante il calcolo della catena')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [userId, departmentId, date])

  return (
    <Card variant="outlined">
      <CardHeader
        icon="account_tree"
        title="Anteprima catena"
        headingLevel={3}
        actions={
          <>
            <Select
              label="Settore"
              value={departmentId}
              onChange={e => setDepartmentId(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <option value="">Seleziona…</option>
              {activeDepartments.map(d => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </Select>
            <TextField
              type="date"
              label="Data"
              value={date}
              onChange={e => setDate(e.target.value)}
            />
          </>
        }
      />
      <CardContent>
        {departmentId === '' ? (
          <div className="text-muted md-typescale-body-small">
            Seleziona un settore e una data per vedere la catena di riparto risolta REQ-058-012 alla
            data indicata.
          </div>
        ) : loading ? (
          <LoadingState />
        ) : error ? (
          <Callout tone="error">{error}</Callout>
        ) : result ? (
          <>
            {result.truncation !== 'none' && (
              <Callout tone="warning">
                {CHAIN_TRUNCATION_MESSAGES[result.truncation]}
              </Callout>
            )}
            {result.nodes.length === 0 ? (
              // TASK-0263 micro-correction (independent reviewer): when the walk was truncated
              // (e.g. AssegnazioniIncoerenti at the seller itself), the warning banner above already
              // explains why there is nothing to show — a generic "nessun nodo risolto" underneath it
              // is redundant noise that dilutes the real explanation, not a second, competing one.
              result.truncation === 'none' && (
                <div className="text-muted md-typescale-body-small mb-0">Nessun nodo risolto per questa combinazione.</div>
              )
            ) : (
              <ol className="mb-0 ps-3">
                {result.nodes.map((node, index) => (
                  <li key={`${node.userId}-${index}`} className="mb-1">
                    <strong>{userName(node.userId)}</strong> — {roleName(node.commissionRoleId)}
                    {node.paysDifferential && (
                      <>
                        {' '}
                        <span title="Paga il differenziale rispetto al nodo sottostante">
                          <StatusPill tone="neutral">differenziale</StatusPill>
                        </span>
                      </>
                    )}
                    {' — '}
                    <ChainNodePercentage node={node} />
                  </li>
                ))}
              </ol>
            )}
          </>
        ) : null}
      </CardContent>
    </Card>
  )
}

export function CommissionAssignmentsPage() {
  const [auth, setAuth] = useState<LoginResponse | null>(null)
  useEffect(() => {
    fetchMe().then(setAuth).catch(() => setAuth(null))
  }, [])
  const canAdminister = isAdmin(auth)

  const [departments, setDepartments] = useState<Department[]>([])
  const [roles, setRoles] = useState<CommissionRole[]>([])
  const [users, setUsers] = useState<DirectoryUser[]>([])
  useEffect(() => {
    fetchDepartments().then(setDepartments).catch(() => setDepartments([]))
    fetchCommissionRoles().then(setRoles).catch(() => setRoles([]))
    userDirectoryApi.list({ activeOnly: false, limit: 200 }).then(setUsers).catch(() => setUsers([]))
  }, [])

  const sortedUsers = useMemo(
    () => [...users].sort((a, b) => a.name.localeCompare(b.name)),
    [users],
  )
  const departmentName = useCallback(
    (id: number) => departments.find(d => d.id === id)?.name ?? `#${id}`,
    [departments],
  )
  const roleName = useCallback(
    (id: number) => roles.find(r => r.id === id)?.name ?? `#${id}`,
    [roles],
  )
  const userName = useCallback(
    (id: number) => users.find(u => u.id === id)?.name ?? `#${id}`,
    [users],
  )

  const [selectedUserId, setSelectedUserId] = useState<number | ''>('')
  const [rows, setRows] = useState<UserCommissionAssignment[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (selectedUserId === '') {
      setRows([])
      return
    }
    setLoading(true)
    setError(null)
    try {
      const result = await fetchCommissionAssignments({ userId: selectedUserId })
      setRows(result)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante il caricamento delle assegnazioni')
    } finally {
      setLoading(false)
    }
  }, [selectedUserId])

  useEffect(() => {
    void load()
  }, [load])

  const sortedRows = useMemo(
    () =>
      [...rows].sort(
        (a, b) =>
          departmentName(a.departmentId).localeCompare(departmentName(b.departmentId)) ||
          b.validFrom.localeCompare(a.validFrom),
      ),
    [rows, departmentName],
  )

  const [showCreate, setShowCreate] = useState(false)
  const [closingRow, setClosingRow] = useState<UserCommissionAssignment | null>(null)
  const [overridingRow, setOverridingRow] = useState<UserCommissionAssignment | null>(null)

  return (
    <Page>
      <PageHeader
        title="Assegnazioni"
        icon="assignment_ind"
        actions={
          canAdminister ? (
            <Button
              variant="filled"
              disabled={selectedUserId === ''}
              onClick={() => setShowCreate(true)}
            >
              Nuova assegnazione
            </Button>
          ) : undefined
        }
      />

      <FilterBar>
        <Select
          label="Utente"
          value={selectedUserId}
          onChange={e => setSelectedUserId(e.target.value === '' ? '' : Number(e.target.value))}
        >
          <option value="">Seleziona un utente…</option>
          {sortedUsers.map(u => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </Select>
      </FilterBar>

      <Callout tone="info">
        Per ogni riga settore × ruolo × manager × periodo viene mostrata la percentuale EFFETTIVA
        (D-01): l'override, quando presente, prevale sempre sul listino aziendale — altrimenti la
        percentuale è quella del listino in vigore oggi, o nell'ultimo giorno coperto
        dall'assegnazione se questa si è già conclusa in passato.
      </Callout>

      {error && <Callout tone="error">{error}</Callout>}

      {selectedUserId !== '' && (
        <ChainPreviewPanel
          userId={selectedUserId}
          departments={departments}
          roleName={roleName}
          userName={userName}
        />
      )}

      {selectedUserId === '' ? (
        <EmptyState icon="person_search" title="Seleziona un utente per vedere le sue assegnazioni." />
      ) : loading ? (
        <LoadingState />
      ) : sortedRows.length === 0 ? (
        <EmptyState icon="assignment_ind" title={`Nessuna assegnazione registrata per ${userName(selectedUserId)}.`} />
      ) : (
        <DataTable>
          <thead>
            <tr>
              <th>Settore</th>
              <th>Ruolo</th>
              <th>Manager</th>
              <th>Dal</th>
              <th>Al</th>
              <th>Percentuale effettiva</th>
              {canAdminister && <th>Azioni</th>}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map(row => {
              const isOpen = row.validTo === null
              return (
                <tr key={row.id}>
                  <td>{departmentName(row.departmentId)}</td>
                  <td>{roleName(row.commissionRoleId)}</td>
                  <td>
                    {row.managerUserId !== null ? (
                      userName(row.managerUserId)
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td>{row.validFrom}</td>
                  <td>{row.validTo ?? <span className="text-muted">in corso</span>}</td>
                  <td>
                    <EffectivePercentageCell row={row} />
                  </td>
                  {canAdminister && (
                    <td>
                      {isOpen && (
                        <div className="d-flex gap-1">
                          <Button variant="text" size="sm" onClick={() => setClosingRow(row)}>
                            Chiudi
                          </Button>
                          <Button variant="tonal" size="sm" onClick={() => setOverridingRow(row)}>
                            Override
                          </Button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </DataTable>
      )}

      {showCreate && selectedUserId !== '' && (
        <CreateAssignmentModal
          userId={selectedUserId}
          departments={departments}
          roles={roles}
          users={users}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false)
            void load()
          }}
        />
      )}

      {closingRow && (
        <CloseAssignmentModal
          row={closingRow}
          onClose={() => setClosingRow(null)}
          onClosed={() => {
            setClosingRow(null)
            void load()
          }}
        />
      )}

      {overridingRow && (
        <OverrideAssignmentModal
          row={overridingRow}
          onClose={() => setOverridingRow(null)}
          onOverridden={() => {
            setOverridingRow(null)
            void load()
          }}
        />
      )}
    </Page>
  )
}

function CreateAssignmentModal({
  userId,
  departments,
  roles,
  users,
  onClose,
  onCreated,
}: {
  userId: number
  departments: Department[]
  roles: CommissionRole[]
  users: DirectoryUser[]
  onClose: () => void
  onCreated: () => void
}) {
  const activeDepartments = useMemo(
    () => [...departments].filter(d => d.isActive).sort((a, b) => a.name.localeCompare(b.name)),
    [departments],
  )
  const sortedRoles = useMemo(
    () => [...roles].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)),
    [roles],
  )
  const otherUsers = useMemo(
    () => [...users].filter(u => u.id !== userId).sort((a, b) => a.name.localeCompare(b.name)),
    [users, userId],
  )

  const [departmentId, setDepartmentId] = useState<number | ''>('')
  const [commissionRoleId, setCommissionRoleId] = useState<number | ''>('')
  const [managerUserId, setManagerUserId] = useState<number | ''>('')
  const [validFrom, setValidFrom] = useState(todayIso())
  const [validTo, setValidTo] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  async function handleCreate(): Promise<void> {
    setSaveError('')
    if (departmentId === '' || commissionRoleId === '') {
      setSaveError('Seleziona settore e ruolo.')
      return
    }
    if (!validFrom) {
      setSaveError('Seleziona la data di inizio validità.')
      return
    }

    setSaving(true)
    try {
      await createCommissionAssignment({
        userId,
        departmentId,
        commissionRoleId,
        validFrom,
        validTo: validTo || null,
        managerUserId: managerUserId === '' ? null : managerUserId,
      })
      onCreated()
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Errore durante la creazione della nuova assegnazione')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="Nuova assegnazione"
      supportingText="La nuova assegnazione parte senza override: la percentuale effettiva sarà quella del listino aziendale finché non viene registrato un override esplicito."
      as="form"
      onSubmit={e => { e.preventDefault(); void handleCreate() }}
      actions={
        <>
          <Button variant="text" onClick={onClose}>
            Annulla
          </Button>
          <Button type="submit" variant="text" disabled={saving}>
            {saving ? 'Salvataggio...' : 'Crea assegnazione'}
          </Button>
        </>
      }
    >
      {saveError && <Callout tone="error">{saveError}</Callout>}
      <FormGrid>
        <Select
          label="Settore *"
          value={departmentId}
          onChange={e => setDepartmentId(e.target.value === '' ? '' : Number(e.target.value))}
        >
          <option value="">Seleziona…</option>
          {activeDepartments.map(d => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </Select>
        <Select
          label="Ruolo *"
          value={commissionRoleId}
          onChange={e => setCommissionRoleId(e.target.value === '' ? '' : Number(e.target.value))}
        >
          <option value="">Seleziona…</option>
          {sortedRoles.map(r => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </Select>
        <TextField
          type="date"
          label="Valida dal *"
          value={validFrom}
          onChange={e => setValidFrom(e.target.value)}
        />
        <TextField
          type="date"
          label="Valida fino a (facoltativo)"
          value={validTo}
          onChange={e => setValidTo(e.target.value)}
        />
        <Select
          className="md-span-full"
          label="Manager (facoltativo)"
          value={managerUserId}
          onChange={e => setManagerUserId(e.target.value === '' ? '' : Number(e.target.value))}
          supportingText="D-05: la gerarchia che paga, separata da quella che vede — un utente non può essere manager di sé stesso."
        >
          <option value="">Nessuno</option>
          {otherUsers.map(u => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </Select>
      </FormGrid>
    </Dialog>
  )
}

function CloseAssignmentModal({
  row,
  onClose,
  onClosed,
}: {
  row: UserCommissionAssignment
  onClose: () => void
  onClosed: () => void
}) {
  const [validTo, setValidTo] = useState(todayIso())
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  async function handleClose(): Promise<void> {
    setSaveError('')
    if (!validTo) {
      setSaveError('Seleziona la data di chiusura.')
      return
    }
    setSaving(true)
    try {
      await closeCommissionAssignment(row.id, validTo)
      onClosed()
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Errore durante la chiusura dell’assegnazione')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="Chiudi assegnazione"
      supportingText="Imposta la data di fine validità — la riga resta consultabile nello storico, non viene mai cancellata."
      as="form"
      onSubmit={e => { e.preventDefault(); void handleClose() }}
      actions={
        <>
          <Button variant="text" onClick={onClose}>
            Annulla
          </Button>
          <Button type="submit" variant="text" disabled={saving}>
            {saving ? 'Salvataggio...' : 'Chiudi assegnazione'}
          </Button>
        </>
      }
    >
      {saveError && <Callout tone="error">{saveError}</Callout>}
      <FormGrid columns={1}>
        <TextField
          type="date"
          label="Valida fino a *"
          min={row.validFrom}
          value={validTo}
          onChange={e => setValidTo(e.target.value)}
        />
      </FormGrid>
    </Dialog>
  )
}

function OverrideAssignmentModal({
  row,
  onClose,
  onOverridden,
}: {
  row: UserCommissionAssignment
  onClose: () => void
  onOverridden: () => void
}) {
  const [percentage, setPercentage] = useState(
    row.overridePercentage !== undefined && row.overridePercentage !== null
      ? String(row.overridePercentage)
      : '',
  )
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  async function handleOverride(): Promise<void> {
    setSaveError('')
    const percentageValue = Number(percentage.replace(',', '.'))
    if (!percentage.trim() || Number.isNaN(percentageValue)) {
      setSaveError('Inserisci una percentuale valida (0–100).')
      return
    }
    if (!reason.trim()) {
      setSaveError('La motivazione è obbligatoria.')
      return
    }

    setSaving(true)
    try {
      await overrideCommissionAssignment(row.id, {
        overridePercentage: percentageValue,
        overrideReason: reason.trim(),
      })
      onOverridden()
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Errore durante la registrazione dell’override')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="Override provvigione"
      supportingText="REQ-183-001: l'override è un'eccezione esplicita, datata e approvata rispetto al listino (D-01) — l'approvatore sarà registrato automaticamente come l'amministratore corrente, con data e ora di questo momento. Non modifica il listino aziendale."
      as="form"
      onSubmit={e => { e.preventDefault(); void handleOverride() }}
      actions={
        <>
          <Button variant="text" onClick={onClose}>
            Annulla
          </Button>
          <Button type="submit" variant="tonal" disabled={saving}>
            {saving ? 'Salvataggio...' : 'Registra override'}
          </Button>
        </>
      }
    >
      {saveError && <Callout tone="error">{saveError}</Callout>}
      <FormGrid columns={1}>
        <TextField
          type="number"
          step="0.01"
          min="0"
          max="100"
          label="Percentuale override (%) *"
          value={percentage}
          onChange={e => setPercentage(e.target.value)}
        />
        <TextField
          multiline
          rows={2}
          label="Motivazione *"
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="Es. accordo commerciale rinegoziato con Direttore Z"
        />
      </FormGrid>
    </Dialog>
  )
}
